'use client';

import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';

import { useDistanceContext } from '@/hooks/use-distance-context';
import { LISTING_PAGE } from '@/lib/constants';
import { useOasApp } from '@/providers';

import { AutocompleteQueries } from '../landing/landing-search.types';
import {
  ALL_FACILITY_OPTION,
  DEFAULT_LANGUAGE,
  DEFAULT_LOCATION_FILTER,
  DEFAULT_REQ_PAYLOAD,
  LISTING_FILTERS_STORAGE_KEY,
} from './constants';
import { Facilities, ListingData, ListingRequest, ListingState } from './listing.types';
import { getDefaultSearchValue, getQueryParams, scrollDoctorListingInView } from './utils';

export const ListingContext = createContext<ListingState | undefined>(undefined);

export const ListingProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const { specialties, isLocation } = useOasApp();
  const router = useRouter();
  const pathname = usePathname();
  const defaultQueryParams = getQueryParams();
  const defaultSearchValue = getDefaultSearchValue(specialties);

  const { location, setLocation } = useDistanceContext();
  const { zip, latitude, longitude } = location || { zip: '', latitude: null, longitude: null };

  const [searchValue, setSearchValue] = useState<ListingData>(defaultSearchValue);

  const specialtyId =
    searchValue?.type === AutocompleteQueries.specialtyId ? searchValue?.value : undefined;
  const specialtyName =
    searchValue?.type === AutocompleteQueries.specialtyId ? searchValue?.label : undefined;
  const keywordExact =
    searchValue?.type === AutocompleteQueries.keywordExact ? searchValue?.value : undefined;
  const locationUrlTitle =
    searchValue?.type === AutocompleteQueries.locationUrlTitle ? searchValue?.value : undefined;
  const locationUrlTitleLabel =
    searchValue?.type === AutocompleteQueries.locationUrlTitle ? searchValue?.label : undefined;

  const [selectedGenders, setSelectedGenders] = useState<string[]>(
    defaultQueryParams?.selectedGenders || []
  );
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(
    defaultQueryParams?.selectedLanguages || [DEFAULT_LANGUAGE]
  );
  const [showAcceptingNewPatientsOnly, setShowAcceptingNewPatientsOnly] = useState(
    defaultQueryParams?.showAcceptingNewPatientsOnly || false
  );
  const [teleMedicineFlg, setTeleMedicineFlg] = useState(
    defaultQueryParams?.teleMedicineFlg || false
  );
  const [selectedFacility, setSelectedFacility] = useState(
    defaultQueryParams?.facilityCoid || ALL_FACILITY_OPTION?.coid
  );

  // split value of the selected facility: coid|locationUrlTitle
  const selectedFacilityValuePair = selectedFacility.split('|');
  const selectedFacilityCoid =
    selectedFacilityValuePair.length > 0 ? selectedFacilityValuePair[0] : undefined;
  const selectedFacilityLocationUrlTitle =
    selectedFacilityValuePair.length > 1 ? selectedFacilityValuePair[1] : undefined;

  const initialParams = {
    gender: selectedGenders.length > 1 ? null : selectedGenders?.[0] || null,
    languages: selectedLanguages?.length > 0 ? selectedLanguages.join(';') : null,
    showAcceptingNewPatientsOnly,
    teleMedicineFlg,
    facilityCoid:
      (selectedFacility === ALL_FACILITY_OPTION.coid ? null : selectedFacilityCoid) || null,
    zip,
    latitude,
    longitude,
    keywordExact,
    keywords: defaultQueryParams?.keywords || null,
    locationUrlTitle: defaultQueryParams?.locationUrlTitle || selectedFacilityLocationUrlTitle,
    specialtyId,
    specialty: specialtyName,
  };

  const [appliedFilters, setAppliedFilters] = useState<ListingRequest>({
    ...DEFAULT_REQ_PAYLOAD,
    ...initialParams,
  });

  // Store applied filters in sessionStorage whenever they change
  // This allows breadcrumbs to persist filter state when navigating away from listing
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.setItem(LISTING_FILTERS_STORAGE_KEY, JSON.stringify(appliedFilters));
      } catch (error) {
        console.error('Failed to store listing filters:', error);
      }
    }
  }, [appliedFilters]);

  const [languages, setLanguages] = useState<string[]>([]);
  const [facilities, setFacilities] = useState<Facilities>([]);

  const toggleShowAcceptingNewPatientsOnly = () => {
    setShowAcceptingNewPatientsOnly((p) => !p);
  };

  const toggleTeleMedicineFlg = () => {
    setTeleMedicineFlg((p) => !p);
  };

  const toggleSelectedGenders = (value: string) => {
    setSelectedGenders((prev) =>
      prev.includes(value) ? prev.filter((gender) => gender !== value) : [...prev, value]
    );
  };

  const toggleSelectedLanguages = (value: string) => {
    setSelectedLanguages((prev) =>
      prev.includes(value) ? prev.filter((language) => language !== value) : [...prev, value]
    );
  };

  const specialtyPathParamExists = pathname.includes('/specialty/');

  const clearFilters = useCallback(() => {
    setSearchValue(undefined);
    setLocation(DEFAULT_LOCATION_FILTER);
    setSelectedGenders([]);
    setSelectedLanguages([]);
    setShowAcceptingNewPatientsOnly(false);
    setTeleMedicineFlg(false);
    setSelectedFacility(ALL_FACILITY_OPTION.coid);
    setAppliedFilters(DEFAULT_REQ_PAYLOAD);
    scrollDoctorListingInView();

    // check if location level site
    const isLocationFallback = window.location.pathname.includes('/locations');
    if (isLocation || isLocationFallback) {
      const pathname = window.location.pathname;

      // Todo: check if we have to do exclude for dotcms hosts
      window.history.replaceState(null, '', pathname);
      return;
    }

    // cleanup specialty path param
    if (specialtyPathParamExists) {
      router.push(
        {
          pathname: pathname.split('/specialty/')[0] + LISTING_PAGE,
          query: {},
        },
        undefined,
        { shallow: true }
      );
      return;
    }
    router.push(
      {
        pathname: pathname,
        query: {},
      },
      undefined,
      { shallow: true, scroll: false }
    );
  }, [setLocation, router, pathname, specialtyPathParamExists, isLocation]);

  const contextValues = useMemo(
    () => ({
      selectedGenders,
      toggleSelectedGenders,
      selectedLanguages,
      toggleSelectedLanguages,
      showAcceptingNewPatientsOnly,
      toggleShowAcceptingNewPatientsOnly,
      teleMedicineFlg,
      toggleTeleMedicineFlg,
      selectedFacility,
      setSelectedFacility,
      appliedFilters,
      setAppliedFilters,
      languages,
      setLanguages,
      facilities,
      setFacilities,
      clearFilters,
      searchValue,
      setSearchValue,
      specialtyId,
      specialtyName,
      keywordExact,
      locationUrlTitle,
      locationUrlTitleLabel,
      zip,
    }),
    [
      selectedGenders,
      selectedLanguages,
      showAcceptingNewPatientsOnly,
      teleMedicineFlg,
      selectedFacility,
      appliedFilters,
      languages,
      facilities,
      clearFilters,
      searchValue,
      specialtyId,
      specialtyName,
      keywordExact,
      locationUrlTitle,
      locationUrlTitleLabel,
      zip,
    ]
  );

  return <ListingContext.Provider value={contextValues}>{children}</ListingContext.Provider>;
};
